import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import DOMPurify from 'dompurify'
import mermaid from 'mermaid'
import { Copy, Check, Maximize2, X } from 'lucide-react'
import { cn } from '@/shared/lib/cn'
import { createPortal } from 'react-dom'

// Initialize mermaid once with a dark theme that matches the app's zinc palette
mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  themeVariables: {
    darkMode: true,
    background: '#18181b',
    primaryColor: '#3f3f46',
    primaryTextColor: '#f4f4f5',
    primaryBorderColor: '#52525b',
    lineColor: '#71717a',
    secondaryColor: '#27272a',
    tertiaryColor: '#27272a',
    edgeLabelBackground: '#27272a',
    clusterBkg: '#27272a',
    titleColor: '#f4f4f5',
    nodeBorder: '#52525b',
    mainBkg: '#27272a',
    nodeTextColor: '#f4f4f5',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '13px',
  },
  securityLevel: 'loose',
})

// Unique ID counter for mermaid rendering (mermaid requires unique element IDs)
let mermaidCounter = 0

// ── Markdown artifact ─────────────────────────────────────────────────────────

const markdownClass =
  'prose-sm prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_code]:bg-white/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_pre]:bg-white/10 [&_pre]:p-2 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:text-white [&_a]:text-[#8B96E9] [&_a]:underline [&_hr]:border-white/10 [&_blockquote]:border-l-2 [&_blockquote]:border-white/20 [&_blockquote]:pl-3 [&_blockquote]:text-white/60'

function MarkdownContent({ content }: { content: string }) {
  return (
    <div className={cn('p-3', markdownClass)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {DOMPurify.sanitize(content)}
      </ReactMarkdown>
    </div>
  )
}

// ── HTML artifact ─────────────────────────────────────────────────────────────

function HtmlContent({ content }: { content: string }) {
  const sanitized = DOMPurify.sanitize(content, {
    WHOLE_DOCUMENT: true,
    FORCE_BODY: false,
  })

  return (
    <iframe
      srcDoc={sanitized}
      sandbox="allow-scripts allow-same-origin"
      className="w-full rounded-b-lg bg-white"
      style={{ minHeight: '200px', height: '400px', border: 'none' }}
      title="HTML artifact"
    />
  )
}

// ── Mermaid artifact ──────────────────────────────────────────────────────────

function MermaidContent({ content, fullscreen = false }: { content: string; fullscreen?: boolean }) {
  const [svg, setSvg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const idRef = useRef(`mermaid-${++mermaidCounter}`)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setSvg(null)

    mermaid.render(idRef.current, content).then(({ svg: rendered }) => {
      if (!cancelled) setSvg(rendered)
    }).catch((err: unknown) => {
      if (!cancelled) {
        const msg = err instanceof Error ? err.message : String(err)
        setError(msg)
      }
    })

    return () => { cancelled = true }
  }, [content])

  if (error) {
    return (
      <div className="p-3">
        <div className="text-xs text-red-400/70 mb-2 font-mono">Render error: {error}</div>
        <pre className="text-xs text-white/40 font-mono whitespace-pre-wrap break-all">{content}</pre>
      </div>
    )
  }

  if (!svg) {
    return (
      <div className="p-4 flex items-center justify-center text-xs text-white/30 font-mono">
        Rendering diagram…
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center p-4 overflow-auto',
        fullscreen ? 'min-h-full' : 'min-h-[180px]',
      )}
      // SVG is produced by mermaid — it is safe to inject here
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}

// ── Fullscreen modal ──────────────────────────────────────────────────────────

interface FullscreenModalProps {
  title: string
  content: string
  language: 'html' | 'mermaid' | 'markdown'
  onClose: () => void
}

function FullscreenModal({ title, content, language, onClose }: FullscreenModalProps) {
  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  const modal = (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="flex flex-col flex-1 m-4 rounded-xl border border-zinc-700 bg-zinc-900 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-700 bg-zinc-800/60 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <LanguageBadge language={language} />
            <span className="text-sm font-medium text-zinc-100 truncate">{title}</span>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700 transition-colors flex-shrink-0"
            title="Close (Esc)"
          >
            <X size={15} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {language === 'html' && (
            <iframe
              srcDoc={DOMPurify.sanitize(content, { WHOLE_DOCUMENT: true, FORCE_BODY: false })}
              sandbox="allow-scripts allow-same-origin"
              className="w-full h-full bg-white"
              style={{ border: 'none', minHeight: '100%' }}
              title="HTML artifact (fullscreen)"
            />
          )}
          {language === 'mermaid' && <MermaidContent content={content} fullscreen />}
          {language === 'markdown' && <MarkdownContent content={content} />}
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

// ── Language badge ────────────────────────────────────────────────────────────

function LanguageBadge({ language }: { language: 'html' | 'mermaid' | 'markdown' }) {
  const labels: Record<string, string> = { html: 'HTML', mermaid: 'Mermaid', markdown: 'Markdown' }
  const colors: Record<string, string> = {
    html: 'bg-orange-500/15 text-orange-300 border-orange-500/20',
    mermaid: 'bg-violet-500/15 text-violet-300 border-violet-500/20',
    markdown: 'bg-sky-500/15 text-sky-300 border-sky-500/20',
  }

  return (
    <span className={cn('px-1.5 py-0.5 text-[10px] font-mono rounded border', colors[language])}>
      {labels[language]}
    </span>
  )
}

// ── ArtifactBlock (main export) ───────────────────────────────────────────────

export interface ArtifactBlockProps {
  id?: string
  title: string
  content: string
  language: 'html' | 'mermaid' | 'markdown'
}

export function ArtifactBlock({ title, content, language }: ArtifactBlockProps) {
  const [copied, setCopied] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // silently fail — clipboard may not be available in all contexts
    }
  }

  return (
    <>
      <div className="my-2 rounded-xl border border-zinc-700 bg-zinc-900 overflow-hidden text-sm max-w-full">
        {/* Header bar */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700 bg-zinc-800/60">
          <div className="flex items-center gap-2 min-w-0">
            <LanguageBadge language={language} />
            <span className="text-xs font-medium text-zinc-200 truncate">{title}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            {/* Copy source button */}
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700 rounded transition-colors"
              title="Copy source"
            >
              {copied ? (
                <>
                  <Check size={11} />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy size={11} />
                  <span>Copy</span>
                </>
              )}
            </button>
            {/* Expand button */}
            <button
              onClick={() => setFullscreen(true)}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700 rounded transition-colors"
              title="Expand to fullscreen"
            >
              <Maximize2 size={11} />
              <span>Expand</span>
            </button>
          </div>
        </div>

        {/* Rendered content */}
        <div className="bg-zinc-900">
          {language === 'html' && <HtmlContent content={content} />}
          {language === 'mermaid' && <MermaidContent content={content} />}
          {language === 'markdown' && <MarkdownContent content={content} />}
        </div>
      </div>

      {fullscreen && (
        <FullscreenModal
          title={title}
          content={content}
          language={language}
          onClose={() => setFullscreen(false)}
        />
      )}
    </>
  )
}
